/**
 * Custom Related Products Web Component
 * Uses a dedicated section for clean data retrieval
 * Enhanced with badge support and DYNAMIC metafield descriptions
 * 
 * Bug Fix (05/12/2025): Moved attribute reading to connectedCallback()
 * to prevent stale product ID when navigating between product pages
 */

class CustomRelatedProducts extends HTMLElement {
  constructor() {
    super();
    // Don't read attributes here - they may be stale after DOM updates
    // All attribute reading moved to connectedCallback() for fresh values
  }

  connectedCallback() {
    // Read fresh values every time element connects to DOM
    // This fixes the bug where product ID was stale after page navigation
    this.productId = this.dataset.productId;
    this.limit = parseInt(this.dataset.limit) || 4;
    
    // Badge settings - matching variant picker
    this.showSavingsBadge = this.dataset.showSavingsBadge === 'true';
    this.savingsBadgeType = this.dataset.savingsBadgeType || 'percentage';
    this.customBadgeText = this.dataset.customBadgeText || 'SALE';
    
    // Product metafield setting - FULLY DYNAMIC from block settings
    this.productDescriptionMetafield = this.dataset.productDescriptionMetafield || 'custom.itemised_product';
    
    this.loadRelatedProducts();
  }

  async loadRelatedProducts() {
    try {
      // Use our dedicated custom-foxtan section for clean product data
      const url = `${window.Shopify.routes.root}recommendations/products?section_id=custom-foxtan-related-products&product_id=${this.productId}&limit=${this.limit}`;
      
      console.log('Custom Related Products: Loading from:', url);
      console.log('Custom Related Products: Product ID:', this.productId, 'Limit:', this.limit);
      console.log('Custom Related Products: Looking for metafield:', this.productDescriptionMetafield);
      console.log('Custom Related Products: Badge Settings:', {
        show: this.showSavingsBadge,
        type: this.savingsBadgeType,
        text: this.customBadgeText
      });
      
      // Show loading state
      this.innerHTML = `
        <div class="tft-related-products-block__loading">
          <span class="spinner" aria-label="Loading..."></span>
        </div>
      `;
      
      // Fetch the recommendations
      const response = await fetch(url);
      const html = await response.text();
      
      console.log('Custom Related Products: Response received, length:', html.length);
      
      // Parse the response
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Look for our data container
      const dataContainer = doc.querySelector('.tft-products-data');
      
      if (dataContainer) {
        // Check if it's empty
        if (dataContainer.dataset.empty === 'true') {
          console.log('Custom Related Products: No products returned by API');
          this.renderEmpty();
          return;
        }
        
        const productItems = dataContainer.querySelectorAll('.tft-product-item');
        console.log('Custom Related Products: Found', productItems.length, 'products in dedicated section');
        
        if (productItems.length > 0) {
          const products = [];
          
          // Parse metafield namespace and key from settings
          const metafieldParts = this.productDescriptionMetafield.split('.');
          const namespace = metafieldParts[0] || 'custom';
          const key = metafieldParts[1] || 'itemised_product';
          
          productItems.forEach(item => {
            const product = {
              url: item.dataset.url,
              title: item.dataset.title,
              price: item.dataset.price,
              comparePrice: item.dataset.comparePrice,
              image: item.dataset.image,
              vendor: item.dataset.vendor,
              handle: item.dataset.handle || '',
              description: '' // Will be populated from metafields
            };
            
            // Try to get metafields from embedded JSON
            const metafieldsScript = item.querySelector('.product-metafields');
            if (metafieldsScript) {
              try {
                const metafields = JSON.parse(metafieldsScript.textContent);
                
                // Dynamically access the specified metafield
                if (metafields[namespace] && metafields[namespace][key]) {
                  product.description = metafields[namespace][key];
                  console.log(`Custom Related Products: Found ${namespace}.${key} for ${product.title}:`, product.description);
                } else {
                  console.log(`Custom Related Products: No ${namespace}.${key} found for ${product.title}`);
                  // Log available metafields for debugging
                  if (metafields[namespace]) {
                    console.log(`Available metafields in ${namespace}:`, Object.keys(metafields[namespace]));
                  }
                }
              } catch (e) {
                console.error('Custom Related Products: Error parsing metafields for', product.title, e);
              }
            }
            
            // Only add if we have required data
            if (product.url && product.title) {
              products.push(product);
            }
          });
          
          if (products.length > 0) {
            this.renderProducts(products);
          } else {
            this.renderEmpty();
          }
        } else {
          this.renderEmpty();
        }
      } else {
        // Fallback: Try the standard section approach
        console.log('Custom Related Products: Dedicated section not found, trying fallback');
        this.loadWithStandardSection();
      }
      
    } catch (error) {
      console.error('Custom Related Products: Error loading products', error);
      this.renderError();
    }
  }

  // Fallback method using the standard related-products section
  async loadWithStandardSection() {
    try {
      const url = `${window.Shopify.routes.root}recommendations/products?section_id=related-products&product_id=${this.productId}&limit=${this.limit}`;
      
      const response = await fetch(url);
      const html = await response.text();
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Look for the shopify-section
      const section = doc.querySelector('.shopify-section');
      if (!section) {
        this.renderEmpty();
        return;
      }
      
      // Find the product-recommendations element
      const recommendations = section.querySelector('product-recommendations');
      if (!recommendations || !recommendations.innerHTML.trim()) {
        this.renderEmpty();
        return;
      }
      
      // Extract products from the grid
      const gridItems = recommendations.querySelectorAll('.f-column, .grid__item');
      console.log('Custom Related Products: Found', gridItems.length, 'grid items');
      
      const products = [];
      gridItems.forEach((item, index) => {
        if (index >= this.limit) return;
        
        const link = item.querySelector('a[href*="/products/"]');
        if (!link) return;
        
        const product = {
          url: link.href || link.getAttribute('href'),
          title: item.querySelector('.card__heading, h3, .full-unstyled-link')?.textContent.trim() || '',
          image: item.querySelector('img')?.src || item.querySelector('img')?.dataset.src || '',
          price: item.querySelector('.price')?.textContent.trim() || '',
          comparePrice: '', // Fallback doesn't have compare price
          description: '' // Fallback doesn't have metafield access
        };
        
        if (product.url && product.title) {
          console.log(`Custom Related Products: Product ${index + 1} - "${product.title}"`);
          products.push(product);
        }
      });
      
      if (products.length > 0) {
        this.renderProducts(products);
      } else {
        this.renderEmpty();
      }
      
    } catch (error) {
      console.error('Custom Related Products: Error in fallback', error);
      this.renderError();
    }
  }

  calculateSavings(price, comparePrice) {
    // Parse money strings to numbers
    const parsePrice = (priceStr) => {
      if (!priceStr || priceStr === '$0.00') return 0;
      // Remove currency symbols and convert to number
      const cleaned = priceStr.replace(/[^0-9.,]/g, '').replace(',', '');
      return parseFloat(cleaned) || 0;
    };
    
    const priceNum = parsePrice(price);
    const comparePriceNum = parsePrice(comparePrice);
    
    if (comparePriceNum <= priceNum || comparePriceNum === 0) {
      return { hasSavings: false, savingsText: '' };
    }
    
    let savingsText = '';
    
    switch (this.savingsBadgeType) {
      case 'percentage':
        const percentage = Math.floor(((comparePriceNum - priceNum) / comparePriceNum) * 100);
        savingsText = `Save ${percentage}%`;
        break;
      case 'fixed':
        const amount = comparePriceNum - priceNum;
        // Format as currency - simple version
        savingsText = `Save ${this.formatMoney(amount)}`;
        break;
      case 'custom':
        savingsText = this.customBadgeText;
        break;
      default:
        savingsText = 'SALE';
    }
    
    return { hasSavings: true, savingsText };
  }
  
  formatMoney(amount) {
    // Simple money formatting
    return `$${amount.toFixed(2)}`;
  }

  renderProducts(products) {
    const showVendor = this.dataset.showVendor === 'true';
    
    let html = `
      <div class="tft-related-products-block__content tft-related-products-block__content--animated">
        <div class="tft-related-products-block__cards">
    `;
    
    products.forEach(product => {
      // Calculate savings for badge
      const { hasSavings, savingsText } = this.calculateSavings(product.price, product.comparePrice);
      
      // Use exact same class structure as variant picker for pricing
      let priceHtml = '';
      if (product.comparePrice && product.comparePrice !== '$0.00' && product.comparePrice !== product.price) {
        // Match variant picker structure exactly
        priceHtml = `
          <span class="tft-card-variant__price tft-card-variant__price--compare">
            ${product.comparePrice}
          </span>
          <span class="tft-card-variant__price tft-card-variant__price--current">
            ${product.price}
          </span>
        `;
      } else {
        priceHtml = `<span class="tft-card-variant__price">${product.price}</span>`;
      }
      
      // Badge HTML - matching variant picker exactly
      let badgeHtml = '';
      if (this.showSavingsBadge && hasSavings) {
        badgeHtml = `
          <div class="tft-card-variant__badge">
            <span class="f-badge f-badge--sale">${savingsText}</span>
          </div>
        `;
      }
      
      html += `
        <div class="tft-related-product-card">
          <a href="${product.url}" class="tft-related-product-card__link">
            <div class="tft-related-product-card__content">
              <div class="tft-related-product-card__col tft-related-product-card__col--image">
                ${product.image 
                  ? `<img src="${product.image}" alt="${product.title}" class="tft-related-product-card__image" loading="lazy" width="60" height="70">` 
                  : '<div class="tft-related-product-card__image-placeholder"></div>'
                }
              </div>
              <div class="tft-related-product-card__col tft-related-product-card__col--details">
                ${showVendor && product.vendor ? `<div class="tft-related-product-card__vendor">${product.vendor}</div>` : ''}
                <div class="tft-card-variant__title">${product.title}</div>
                ${product.description ? `
                  <div class="tft-card-variant__description">
                    ${product.description.replace(/\n/g, '<br>')}
                  </div>
                ` : ''}
              </div>
              <div class="tft-card-variant__col tft-card-variant__col--pricing">
                <div class="tft-card-variant__prices">
                  ${priceHtml}
                </div>
                ${badgeHtml}
              </div>
            </div>
          </a>
        </div>
      `;
    });
    
    html += `
        </div>
      </div>
    `;
    
    this.innerHTML = html;
  }

  renderEmpty() {
    // Hide the entire parent block (including heading) when no products
    const parentBlock = this.closest('.tft-related-products-block');
    if (parentBlock) {
      parentBlock.style.display = 'none';
    }
    this.innerHTML = '';
  }

  renderError() {
    this.innerHTML = `
      <div class="tft-related-products-block__error">
        <p>Unable to load related products.</p>
      </div>
    `;
  }
}

// Register the custom element
if (!customElements.get('custom-foxtan-related-products')) {
  customElements.define('custom-foxtan-related-products', CustomRelatedProducts);
}
